import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Select,
    SimpleGrid,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Input,
    VStack,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { AddressLookupInput, AddressLookupResult, useAddressLookup, useStates } from "../../api";
import { SetPageTitle } from "../../components/SetPageTitle";

type InputMode = "single" | "parts";

interface AddressLookupFormValues {
    addressLine: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
}

const defaultValues: AddressLookupFormValues = {
    addressLine: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
};

const trimValue = (value?: string) => value?.trim() ?? "";

export const AddressLookupPage: FunctionComponent = () => {
    const appInsights = useAppInsightsContext();
    const states = useStates();
    const lookup = useAddressLookup();
    const [inputMode, setInputMode] = useState<InputMode>("single");
    const [lookupFailed, setLookupFailed] = useState(false);
    const [result, setResult] = useState<AddressLookupResult>();
    const lastInputSignature = useRef<string>();
    const form = useForm<AddressLookupFormValues>({ defaultValues, mode: "onChange" });

    const {
        formState: { errors },
        handleSubmit,
        clearErrors,
        register,
        watch,
    } = form;

    const addressLine = watch("addressLine");
    const street1 = watch("street1");
    const street2 = watch("street2");
    const city = watch("city");
    const state = watch("state");
    const zipCode = watch("zipCode");

    useEffect(() => {
        appInsights.trackPageView({ uri: "/admin/address-lookup" });
    }, [appInsights]);

    const inputSignature = [inputMode, addressLine, street1, street2, city, state, zipCode].join("|");

    useEffect(() => {
        if (lastInputSignature.current !== undefined && lastInputSignature.current !== inputSignature) {
            clearErrors();
            setLookupFailed(false);
            setResult(undefined);
        }
        lastInputSignature.current = inputSignature;
    }, [clearErrors, inputSignature]);

    const stateOptions = useMemo(
        () =>
            (states.data ?? []).map(option => (
                <option key={option.code} value={option.code}>
                    {option.name}
                </option>
            )),
        [states.data]
    );

    const isSingleLineReady = trimValue(addressLine).length > 0;
    const isAddressPartsReady =
        trimValue(street1).length > 0 && trimValue(city).length > 0 && trimValue(state).length > 0 && /^\d{5}$/.test(trimValue(zipCode));

    const submitLookup = async (values: AddressLookupFormValues) => {
        const payload: AddressLookupInput =
            inputMode === "single"
                ? {
                      addressLine: trimValue(values.addressLine),
                  }
                : {
                      street1: trimValue(values.street1),
                      street2: trimValue(values.street2) || undefined,
                      city: trimValue(values.city),
                      state: trimValue(values.state),
                      zipCode: trimValue(values.zipCode),
                  };

        try {
            setLookupFailed(false);
            setResult(await lookup.mutateAsync(payload));
        } catch {
            setLookupFailed(true);
            setResult(undefined);
        }
    };

    const addressLines = result ? [result.street1, result.street2].filter((line): line is string => !!line) : [];
    const cityStateZip = result ? [result.city, result.state, result.zipCode].filter((line): line is string => !!line).join(", ") : "";

    return (
        <Box h="100%" overflow="auto" bg="gray.50">
            <SetPageTitle title="System Settings" />
            <Flex align="center" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                <Text fontSize="xl" fontWeight="semibold">
                    Address Lookup
                </Text>
            </Flex>

            <Box px={6} py={4}>
                <VStack align="stretch" gap={4}>
                    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={5}>
                        <VStack as="form" noValidate align="stretch" gap={5} onSubmit={handleSubmit(submitLookup)}>
                            <Box>
                                <Text fontSize="sm" color="gray.600">
                                    Look up and preview address verification details without saving any data.
                                </Text>
                            </Box>
                            <Tabs
                                variant="enclosed"
                                colorScheme="blue"
                                index={inputMode === "single" ? 0 : 1}
                                onChange={index => setInputMode(index === 0 ? "single" : "parts")}
                            >
                                <TabList>
                                    <Tab>Single Line</Tab>
                                    <Tab>Address Parts</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel px={0} pb={0} pt={4}>
                                        <VStack align="stretch" gap={4}>
                                            <FormControl isRequired isInvalid={!!errors.addressLine}>
                                                <FormLabel>Address</FormLabel>
                                                <Input
                                                    placeholder="123 Armory St, Springfield, IL 62701"
                                                    maxLength={500}
                                                    {...register("addressLine", {
                                                        validate: value =>
                                                            inputMode !== "single" || trimValue(value).length > 0 || "A full address is required",
                                                    })}
                                                />
                                                <FormErrorMessage>{errors.addressLine?.message}</FormErrorMessage>
                                            </FormControl>
                                            <Flex justify="flex-end">
                                                <Button colorScheme="blue" type="submit" isLoading={lookup.isLoading} isDisabled={!isSingleLineReady}>
                                                    Look Up
                                                </Button>
                                            </Flex>
                                        </VStack>
                                    </TabPanel>
                                    <TabPanel px={0} pb={0} pt={4}>
                                        <VStack align="stretch" gap={4}>
                                            {states.isError && (
                                                <Alert status="warning" borderRadius="md">
                                                    <AlertIcon />
                                                    <AlertDescription>The list of states could not be loaded. Refresh and try again.</AlertDescription>
                                                </Alert>
                                            )}
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                <FormControl gridColumn={{ md: "span 2" }} isRequired isInvalid={!!errors.street1}>
                                                    <FormLabel>Street 1</FormLabel>
                                                    <Input
                                                        maxLength={255}
                                                        {...register("street1", {
                                                            validate: value => inputMode !== "parts" || trimValue(value).length > 0 || "Street 1 is required",
                                                        })}
                                                    />
                                                    <FormErrorMessage>{errors.street1?.message}</FormErrorMessage>
                                                </FormControl>
                                                <FormControl gridColumn={{ md: "span 2" }}>
                                                    <FormLabel>Street 2</FormLabel>
                                                    <Input maxLength={255} {...register("street2")} />
                                                </FormControl>
                                                <FormControl isRequired isInvalid={!!errors.city}>
                                                    <FormLabel>City</FormLabel>
                                                    <Input
                                                        maxLength={255}
                                                        {...register("city", {
                                                            validate: value => inputMode !== "parts" || trimValue(value).length > 0 || "City is required",
                                                        })}
                                                    />
                                                    <FormErrorMessage>{errors.city?.message}</FormErrorMessage>
                                                </FormControl>
                                                <FormControl isRequired isInvalid={!!errors.state}>
                                                    <FormLabel>State</FormLabel>
                                                    <Select
                                                        placeholder="Select state"
                                                        {...register("state", {
                                                            validate: value => inputMode !== "parts" || trimValue(value).length > 0 || "State is required",
                                                        })}
                                                    >
                                                        {stateOptions}
                                                    </Select>
                                                    <FormErrorMessage>{errors.state?.message}</FormErrorMessage>
                                                </FormControl>
                                                <FormControl isRequired isInvalid={!!errors.zipCode}>
                                                    <FormLabel>ZIP code</FormLabel>
                                                    <Input
                                                        maxLength={5}
                                                        inputMode="numeric"
                                                        {...register("zipCode", {
                                                            validate: value => {
                                                                if (inputMode !== "parts") {
                                                                    return true;
                                                                }
                                                                if (!trimValue(value).length) {
                                                                    return "ZIP code is required";
                                                                }
                                                                return /^\d{5}$/.test(trimValue(value)) || "ZIP code must be 5 digits";
                                                            },
                                                        })}
                                                    />
                                                    <FormErrorMessage>{errors.zipCode?.message}</FormErrorMessage>
                                                </FormControl>
                                            </SimpleGrid>
                                            <Flex justify="flex-end">
                                                <Button colorScheme="blue" type="submit" isLoading={lookup.isLoading} isDisabled={!isAddressPartsReady || states.isLoading}>
                                                    Look Up
                                                </Button>
                                            </Flex>
                                        </VStack>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </VStack>
                    </Box>

                    {lookup.isLoading && (
                        <Flex bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={6} justify="center" align="center" gap={3}>
                            <Spinner />
                            <Text color="gray.600">Looking up address...</Text>
                        </Flex>
                    )}

                    {lookupFailed && !lookup.isLoading && (
                        <Alert status="error" borderRadius="md" bg="white" border="1px solid" borderColor="red.200">
                            <AlertIcon />
                            <AlertDescription>Address lookup could not be completed. Review the address and try again.</AlertDescription>
                        </Alert>
                    )}

                    {result?.error && (
                        <Alert status="error" borderRadius="md" bg="white" border="1px solid" borderColor="red.200">
                            <AlertIcon />
                            <AlertDescription>{result.error}</AlertDescription>
                        </Alert>
                    )}

                    {result && !result.error && !result.isMatched && (
                        <Alert status="info" borderRadius="md" bg="white" border="1px solid" borderColor="gray.200">
                            <AlertIcon />
                            <AlertDescription>No match found for the submitted address.</AlertDescription>
                        </Alert>
                    )}

                    {result && !result.error && result.isMatched && (
                        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={5}>
                            <VStack align="stretch" gap={4}>
                                <Flex justify="space-between" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3}>
                                    <Text fontSize="lg" fontWeight="semibold">
                                        Lookup Result
                                    </Text>
                                    <HStack gap={2} flexWrap="wrap">
                                        <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2}>
                                            Matched
                                        </Badge>
                                        {result.zoneStatus === "In" && (
                                            <Badge colorScheme="green" variant="solid" borderRadius="full" px={2}>
                                                {result.zoneName ? `In Zone: ${result.zoneName}` : "In Zone"}
                                            </Badge>
                                        )}
                                        {result.zoneStatus === "Out" && (
                                            <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                                                Not in Zone
                                            </Badge>
                                        )}
                                    </HStack>
                                </Flex>
                                <Box>
                                    {addressLines.map(line => (
                                        <Text key={line} fontWeight="medium">
                                            {line}
                                        </Text>
                                    ))}
                                    {cityStateZip && <Text color="gray.700">{cityStateZip}</Text>}
                                </Box>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                        <Text fontSize="xs" textTransform="uppercase" color="gray.500" fontWeight="semibold" letterSpacing="wide">
                                            County
                                        </Text>
                                        <Text color="gray.700">{result.county || "—"}</Text>
                                    </Box>
                                    {(result.latitude !== null || result.longitude !== null) && (
                                        <Box>
                                            <Text fontSize="xs" textTransform="uppercase" color="gray.500" fontWeight="semibold" letterSpacing="wide">
                                                Coordinates
                                            </Text>
                                            <Text color="gray.700" fontSize="sm">
                                                {result.latitude ?? "—"}, {result.longitude ?? "—"}
                                            </Text>
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </VStack>
                        </Box>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};
